// @flow
import NumberFormat from 'react-number-format';
import postscribe from 'postscribe';

import CheckoutErrors from '@components/cart/CheckoutErrors';

import QuestionMarkPopup from '@components/popups/QuestionMark';
import LinkPopup from '@components/popups/Link';
import * as CVVContent from '@components/popups/content/CVV';
import * as PrivacyPolicyContent from '@components/popups/content/PrivacyPolicy';

const checkmarkIcon = '/images/checkout_checkmark_icon.png';

declare var Rollbar: any;

const billingYears = (count) => {
  var date = new Date();
  var years = [date.getFullYear()];

  while (years.length < count) {
    years = [...years, years[years.length - 1] + 1];
  }

  return years;
};

type InputRefType = {
  current: null | React.ElementRef<'input'>
};

type SelectRefType = {
  current: null | React.ElementRef<'select'>
};

type DivRefType = {
  current: null | React.ElementRef<'div'>
};

type ErrorFieldType = string;

type PropsType = {
  onSubmitClick: (fields: Object, allowNext?: boolean) => void,
  validateCardData: (
    paymentData: { [string]: string },
    callback: Function
  ) => void,
  onShipToBillingChange: (e: SyntheticMouseEvent<HTMLInputElement>) => void,
  active: boolean,
  completed: boolean,
  editStep: (e: SyntheticMouseEvent<HTMLLinkElement>, name: string) => void,
  stepName: string,
  errorFields: Array<ErrorFieldType>
};

type StateType = {};

class CheckoutStepTwo extends React.Component<PropsType, StateType> {
  cardErrorRef: DivRefType;
  cardNumberRef: InputRefType;
  cardCVVRef: InputRefType;
  cardExpMonthRef: SelectRefType;
  cardExpYearRef: SelectRefType;
  billingAddressRef: InputRefType;
  billingAddress2Ref: InputRefType;
  billingCityRef: InputRefType;
  billingStateRef: SelectRefType;
  billingZipCodeRef: InputRefType;
  billingPhoneRef: InputRefType;
  otherRefs: Array<InputRefType | SelectRefType>;
  billingRefs: Array<InputRefType | SelectRefType>;

  constructor (props: PropsType) {
    super(props);

    this.cardErrorRef = React.createRef();
    this.cardNumberRef = React.createRef();
    this.cardCVVRef = React.createRef();
    this.cardExpMonthRef = React.createRef();
    this.cardExpYearRef = React.createRef();
    this.billingAddressRef = React.createRef();
    this.billingAddress2Ref = React.createRef();
    this.billingCityRef = React.createRef();
    this.billingStateRef = React.createRef();
    this.billingZipCodeRef = React.createRef();
    this.billingPhoneRef = React.createRef();
    this.otherRefs = [
      this.billingAddressRef,
      this.billingAddress2Ref,
      this.billingCityRef,
      this.billingStateRef,
      this.billingZipCodeRef,
      this.billingPhoneRef
    ];
    this.billingRefs = [
      this.cardNumberRef,
      this.cardCVVRef,
      this.cardExpMonthRef,
      this.cardExpYearRef
    ];
  }

  componentDidMount () {
    if (!window) return;

    postscribe(
      '#AuthorizeNetSeal',
      `
      <script type="text/javascript" language="javascript">
        var ANS_customer_id = "23fa4de0-dfb1-4df4-a8d6-e5f0a8fe4dd5";
      </script>
      <script type="text/javascript" language="javascript" src="//verify.authorize.net/anetseal/seal.js" ></script>
    `
    );
  }

  handleButtonClick = (e: SyntheticMouseEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (
      !this.cardNumberRef.current ||
      !this.cardExpMonthRef.current ||
      !this.cardExpYearRef.current ||
      !this.cardCVVRef.current ||
      !this.cardErrorRef.current ||
      !this.billingZipCodeRef.current
    ) {
      const refsExist = {
        cardNumberRef: typeof this.cardNumberRef.current,
        cardExpMonthRef: typeof this.cardExpMonthRef.current,
        cardExpYearRef: typeof this.cardExpYearRef.current,
        cardCVVRef: typeof this.cardCVVRef.current,
        cardErrorRef: typeof this.cardErrorRef.current,
        billingZipCodeRef: typeof this.billingZipCodeRef.current
      };
      Rollbar.error(`At least one ref is null: ${JSON.stringify(refsExist)}`);
      return;
    }

    this.props.validateCardData(
      {
        cardNumber: this.cardNumberRef.current.value,
        month: this.cardExpMonthRef.current.value,
        year: this.cardExpYearRef.current.value,
        cardCode: this.cardCVVRef.current.value,
        zip: this.billingZipCodeRef.current.value
      },
      (response) => {
        const fields = {};

        if (response.messages.resultCode === 'Error') {
          // $FlowFixMe
          this.cardErrorRef.current.innerText = response.messages.message
            .map((message) => message.text)
            .join(' ');
        } else {
          // $FlowFixMe
          this.cardErrorRef.current.innerText = '';
        }

        // We don't want to hit our server until Authorize.net has responded.
        // That way we can know whether we should allow the form to move to the
        // next step or not.
        this.otherRefs.forEach((ref) => {
          const el = ref.current;
          if (!el) return;
          fields[(el.name.match(/order\[(.*)\]/) || '')[1]] = el.value;
        });

        this.props.onSubmitClick(
          { order: fields },
          response.messages.resultCode === 'Ok'
        );
      }
    );
  };

  render () {
    const {
      active,
      completed,
      onSubmitClick,
      editStep,
      stepName,
      onShipToBillingChange,
      errorFields
    } = this.props;
    return (
      <div id="checkoutBox">
        <h3>
          <img
            alt="Checkmark_icon"
            id="billing_checked"
            src={checkmarkIcon}
            style={{ display: completed ? 'block' : 'none' }}
          />
          Your Billing Information:
          <a
            href="#"
            id="billing_edit_link"
            onClick={(e) => editStep(e, stepName)}
            style={{ display: completed ? 'block' : 'none' }}>
            edit
          </a>
        </h3>
        <div id="checkoutContent">
          <div
            id="billing_checkout"
            style={{ display: active ? 'block' : 'none' }}
            className="clearfix">
            <div className="billing">
              <dl>
                <div>
                  <dt>
                    <label htmlFor="credit_card_type">Card Type:</label>
                  </dt>
                  <dd>
                    <select autoComplete="cc-type" id="credit_card_type">
                      <option value="visa">Visa</option>
                      <option value="master">MasterCard</option>
                      <option value="american_express">American Express</option>
                      <option value="discover">Discover</option>
                    </select>
                  </dd>
                </div>
                <div style={{ position: 'relative' }}>
                  <dt>
                    <label htmlFor="credit_card_number">Number</label>
                  </dt>
                  <dd>
                    <input
                      autoComplete="cc-number"
                      id="credit_card_number"
                      pattern="[0-9]*"
                      type="text"
                      ref={this.cardNumberRef}
                    />
                  </dd>
                  <div id="CVV">
                    <dt style={{ fontWeight: 'normal' }}>CVV:</dt>
                    <dd>
                      <input
                        autoComplete="cc-csc"
                        id="credit_card_verification_value"
                        pattern="[0-9]*"
                        size={5}
                        type="text"
                        ref={this.cardCVVRef}
                      />
                      <QuestionMarkPopup initialContent={CVVContent} />
                    </dd>
                  </div>
                </div>
                <div>
                  <dt>
                    <label htmlFor="credit_card_month">Exp. Date</label>
                  </dt>
                  <dd>
                    <select id="credit_card_month" ref={this.cardExpMonthRef}>
                      <option value>- -</option>
                      <option value="01">1</option>
                      <option value="02">2</option>
                      <option value="03">3</option>
                      <option value="04">4</option>
                      <option value="05">5</option>
                      <option value="06">6</option>
                      <option value="07">7</option>
                      <option value="08">8</option>
                      <option value="09">9</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                    </select>
                    <span> / </span>
                    <select id="credit_card_year" ref={this.cardExpYearRef}>
                      <option>- - -</option>
                      {billingYears(11).map((year) => {
                        return (
                          <option value={year.toString().slice(2)} key={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </dd>
                </div>
                <p>
                  Enter the billing address and phone on file with your card{' '}
                  <br />
                  issuer (the address your monthly statement are mailed to)
                </p>
                <div>
                  <dt>
                    <label htmlFor="order_billing_address">Address (1):</label>
                  </dt>
                  <dd>
                    <input
                      autoComplete="address-line1"
                      id="order_billing_address"
                      name="order[billing_address]"
                      size={30}
                      type="text"
                      ref={this.billingAddressRef}
                    />
                  </dd>
                </div>
                <div>
                  <dt>
                    <label htmlFor="order_billing_address">Address (2):</label>
                  </dt>
                  <dd>
                    <input
                      autoComplete="address-line2"
                      id="order_billing_address2"
                      name="order[billing_address2]"
                      size={30}
                      type="text"
                      ref={this.billingAddress2Ref}
                    />
                  </dd>
                </div>
                <div>
                  <dt>
                    <label htmlFor="order_billing_city">City:</label>
                  </dt>
                  <dd>
                    <input
                      autoComplete="address-level2"
                      id="order_billing_city"
                      name="order[billing_city]"
                      size={30}
                      type="text"
                      ref={this.billingCityRef}
                    />
                  </dd>
                </div>
                <div>
                  <dt>
                    <label htmlFor="order_billing_state">State:</label>
                  </dt>
                  <dd>
                    <select
                      id="order_billing_state"
                      name="order[billing_state]"
                      ref={(el) => {
                        this.billingStateRef.current = el;
                      }}>
                      <option value="">- -</option>
                      <option value="AK">AK</option>
                      <option value="AL">AL</option>
                      <option value="AR">AR</option>
                      <option value="AZ">AZ</option>
                      <option value="CA">CA</option>
                      <option value="CO">CO</option>
                      <option value="CT">CT</option>
                      <option value="DC">DC</option>
                      <option value="DE">DE</option>
                      <option value="FL">FL</option>
                      <option value="GA">GA</option>
                      <option value="HI">HI</option>
                      <option value="IA">IA</option>
                      <option value="ID">ID</option>
                      <option value="IL">IL</option>
                      <option value="IN">IN</option>
                      <option value="KS">KS</option>
                      <option value="KY">KY</option>
                      <option value="LA">LA</option>
                      <option value="MA">MA</option>
                      <option value="MD">MD</option>
                      <option value="ME">ME</option>
                      <option value="MI">MI</option>
                      <option value="MN">MN</option>
                      <option value="MO">MO</option>
                      <option value="MS">MS</option>
                      <option value="MT">MT</option>
                      <option value="NC">NC</option>
                      <option value="ND">ND</option>
                      <option value="NE">NE</option>
                      <option value="NH">NH</option>
                      <option value="NJ">NJ</option>
                      <option value="NM">NM</option>
                      <option value="NV">NV</option>
                      <option value="NY">NY</option>
                      <option value="OH">OH</option>
                      <option value="OK">OK</option>
                      <option value="OR">OR</option>
                      <option value="PA">PA</option>
                      <option value="RI">RI</option>
                      <option value="SC">SC</option>
                      <option value="SD">SD</option>
                      <option value="TN">TN</option>
                      <option value="TX">TX</option>
                      <option value="UT">UT</option>
                      <option value="VA">VA</option>
                      <option value="VT">VT</option>
                      <option value="WA">WA</option>
                      <option value="WI">WI</option>
                      <option value="WV">WV</option>
                      <option value="WY">WY</option>
                    </select>
                  </dd>
                  <div id="zip-code">
                    <dt>
                      <label htmlFor="order_billing_zip_code">Zip:</label>
                    </dt>
                    <dd>
                      <input
                        autoComplete="postal-code"
                        id="order_billing_zip_code"
                        name="order[billing_zip_code]"
                        pattern="[0-9]*"
                        size={8}
                        type="text"
                        ref={this.billingZipCodeRef}
                      />{' '}
                    </dd>
                  </div>
                </div>
                <div>
                  <dt>
                    <label htmlFor="order_billing_phone">Phone:</label>
                  </dt>
                  <dd>
                    <NumberFormat
                      type="tel"
                      format="(###) ###-####"
                      mask="_"
                      id="order_billing_phone"
                      autoComplete="home mobile work tel"
                      name="order[billing_phone(1s)]"
                      size={30}
                      getInputRef={(el) => {
                        this.billingPhoneRef.current = el;
                      }}
                    />
                  </dd>
                </div>
                <div className="shipping-options clearfix">
                  <div>
                    <dt className="shipCheck">Ship to this address?</dt>
                    <dd>
                      <input
                        defaultChecked="checked"
                        id="order_ship_to_billing_true"
                        name="order[ship_to_billing]"
                        defaultValue="true"
                        type="radio"
                        onChange={onShipToBillingChange}
                      />{' '}
                      Yes{' '}
                    </dd>
                    <span>&nbsp;</span>
                    <dd>
                      <input
                        id="order_ship_to_billing_false"
                        name="order[ship_to_billing]"
                        defaultValue="false"
                        type="radio"
                        onChange={onShipToBillingChange}
                      />{' '}
                      No
                    </dd>
                  </div>
                  <div>
                    Orders outside
                    <br />
                    USA,{' '}
                    <a
                      href="/customer-service/shipping/international-shipping"
                      target="_blank">
                      click here
                    </a>
                  </div>
                </div>
                <input
                  className="blue button"
                  defaultValue="Enter"
                  type="button"
                  onClick={this.handleButtonClick}
                />
                <div className="checkoutErrors" ref={this.cardErrorRef} />
                <CheckoutErrors errorFields={errorFields} />
              </dl>
              <input
                id="order_step"
                name="order[step]"
                defaultValue="billing"
                type="hidden"
              />
            </div>
            <div id="billingCheckoutRight">
              <h2>Your information is safe.</h2>
              <LinkPopup initialContent={PrivacyPolicyContent}>
                find out more
              </LinkPopup>

              <div id="security" style={{ margin: '20px auto' }}>
                {/* (c) 2005, 2009. Authorize.Net is a registered trademark of CyberSource Corporation */}
                <div
                  id="AuthorizeNetSeal"
                  style={{ width: 110, margin: '10px 0' }}
                />
              </div>
            </div>
            <div style={{ clear: 'both' }} />
          </div>
        </div>
      </div>
    );
  }
}

export default CheckoutStepTwo;
